import axios from 'axios';

/**
 * Watch plan video stream URLs (HLS / DASH).
 */
export interface WatchPlanVideoUrl {
  hls: string;
  dash: string;
}

/**
 * BBB video attached to a watch plan day.
 */
export interface WatchPlanDayVideo {
  id: string;
  title: string;
  title_native: string;
  book: string;
  chapter_start: number;
  verse_start: number;
  language: string;
  video_url: WatchPlanVideoUrl;
}

/**
 * One day in a watch plan, ordered by day_order.
 */
export interface WatchPlanDay {
  id: string;
  day_order: number;
  day_description: string;
  video: WatchPlanDayVideo;
}

/**
 * Watch plan detail payload from DBS API v2.
 */
export interface WatchPlan {
  id: string;
  banner_url: string;
  plan_title: string;
  plan_short_description: string;
  days: WatchPlanDay[];
}

interface WatchPlanApiResponse {
  success: boolean;
  data: WatchPlan;
  messages: string[];
  errors: string[];
}

/**
 * Temporary slug → Salesforce id map for known watch plans.
 * Key format: `{translationCode}/{slug}` (lowercase).
 */
const WATCH_PLAN_SLUG_TO_ID: Record<string, string> = {
  'aslv/journey-of-prayer-for-the-nations': 'a2FVN000003EAyf2AG',
};

/**
 * Resolve a watch plan Salesforce id from URL translation code + slug.
 *
 * @param {string} code - Translation shortcode from the URL (e.g. aslv).
 * @param {string} slug - Plan slug from the URL.
 * @returns {string | undefined} Salesforce id when mapped; otherwise undefined.
 *
 * @example
 * resolveWatchPlanId('aslv', 'journey-of-prayer-for-the-nations');
 */
export const resolveWatchPlanId = (code: string, slug: string): string | undefined => {
  const key = `${code.toLowerCase()}/${slug.toLowerCase()}`;
  return WATCH_PLAN_SLUG_TO_ID[key];
};

/**
 * Resolve the pretty watch-plan path for a Salesforce id (hides id in the address bar).
 *
 * @param {string} watchPlanId - Watch plan Salesforce id.
 * @returns {string | undefined} Path like `/watch-plans/aslv/slug` when mapped.
 *
 * @example
 * resolveWatchPlanPath('a2FVN000003EAyf2AG');
 */
export const resolveWatchPlanPath = (watchPlanId: string): string | undefined => {
  const entry = Object.entries(WATCH_PLAN_SLUG_TO_ID).find(([, id]) => id === watchPlanId);
  if (entry === undefined) {
    return undefined;
  }

  return `/watch-plans/${entry[0]}`;
};

/**
 * Fetch a watch plan by Salesforce id.
 *
 * @param {string} watchPlanId - Watch plan Salesforce id.
 * @returns {Promise<WatchPlan | undefined>} Plan data on success; undefined on failure.
 *
 * @example
 * const plan = await getWatchPlan('a2FVN000003EAyf2AG');
 *
 * @see src/hooks/useLoadWatchPlan.ts
 */
export const getWatchPlan = async (watchPlanId: string): Promise<WatchPlan | undefined> => {
  try {
    const baseUrl = process.env.REACT_APP_DEAFBIBLESOCIETY_API_V2;
    if (baseUrl === undefined) {
      throw new Error('REACT_APP_DEAFBIBLESOCIETY_API_V2 is not set');
    }

    const { data } = await axios.get<WatchPlanApiResponse>(
      `${baseUrl}/v1/deafbible/watch-plans/${watchPlanId}`
    );

    if (data.success && data.data) {
      return data.data;
    }
  } catch {
    return undefined;
  }

  return undefined;
};
